import React, { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { buildProposalHTML } from '@/lib/proposalDoc';
import { Loader2, FileWarning, CheckCircle2, Printer } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function PublicProposalPage() {
  const { token } = useParams();
  const iframeRef = useRef(null);
  const [state, setState] = useState({ loading: true, expired: false, error: false, html: '', title: '' });
  const [status, setStatus] = useState('sent');
  const [acceptedAt, setAcceptedAt] = useState(null);
  const [acceptedName, setAcceptedName] = useState('');
  const [nameInput, setNameInput] = useState('');
  const [accepting, setAccepting] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/public/proposals/${token}`);
        if (!res.ok) {
          if (active) setState({ loading: false, error: true, expired: false, html: '', title: '' });
          return;
        }
        const data = await res.json();
        if (!active) return;
        if (data.expired) {
          setState({ loading: false, expired: true, error: false, html: '', title: data.title || 'Proposta' });
          return;
        }
        const p = data.proposal || {};
        setStatus(p.status || 'sent');
        setAcceptedAt(p.accepted_at || null);
        setAcceptedName(p.accepted_name || '');
        const html = buildProposalHTML(p, {
          company: data.company || {},
          tenant: data.tenant || {},
          defaultClauses: p.contract_clauses || '',
          autoPrint: false,
        });
        setState({ loading: false, expired: false, error: false, html, title: p.title || 'Proposta' });
        document.title = p.title || 'Proposta';
      } catch (e) {
        if (active) setState({ loading: false, error: true, expired: false, html: '', title: '' });
      }
    })();
    return () => { active = false; };
  }, [token]);

  const handleAccept = async () => {
    setAccepting(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/public/proposals/${token}/accept`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: nameInput }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setStatus('accepted');
        setAcceptedAt(data.accepted_at);
        setAcceptedName(nameInput);
      }
    } catch (e) { /* noop */ } finally {
      setAccepting(false);
    }
  };

  const handlePrint = () => {
    try { iframeRef.current?.contentWindow?.print(); } catch (e) { /* noop */ }
  };

  const fmtDate = (iso) => {
    if (!iso) return '';
    try { return new Date(iso).toLocaleString('pt-BR'); } catch (e) { return ''; }
  };

  if (state.loading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-slate-100 text-slate-500">
        <Loader2 className="w-8 h-8 animate-spin mb-3" />
        Carregando proposta...
      </div>
    );
  }

  if (state.error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-slate-100 text-slate-600 p-6 text-center" data-testid="public-proposal-error">
        <FileWarning className="w-12 h-12 mb-3 text-slate-400" />
        <h1 className="text-xl font-semibold mb-1">Proposta não encontrada</h1>
        <p className="text-sm text-slate-500">O link pode estar incorreto ou a proposta foi removida.</p>
      </div>
    );
  }

  if (state.expired) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-slate-100 text-slate-600 p-6 text-center" data-testid="public-proposal-expired">
        <FileWarning className="w-12 h-12 mb-3 text-amber-500" />
        <h1 className="text-xl font-semibold mb-1">Proposta expirada</h1>
        <p className="text-sm text-slate-500">A validade desta proposta venceu. Entre em contato para solicitar uma atualização.</p>
      </div>
    );
  }

  const accepted = status === 'accepted';

  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
      <iframe
        ref={iframeRef}
        title={state.title}
        srcDoc={state.html}
        data-testid="public-proposal-frame"
        style={{ border: 'none', width: '100%', flex: 1 }}
      />
      <div className="border-t bg-white shadow-[0_-4px_12px_rgba(0,0,0,0.06)] px-4 py-3" data-testid="public-accept-bar">
        {accepted ? (
          <div className="flex items-center justify-center gap-2 text-green-700 font-medium py-1" data-testid="public-accepted-banner">
            <CheckCircle2 className="w-5 h-5" />
            <span>Proposta aceita{acceptedName ? ` por ${acceptedName}` : ''} em {fmtDate(acceptedAt)}</span>
          </div>
        ) : (
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 max-w-3xl mx-auto">
            <input
              value={nameInput}
              onChange={(e) => setNameInput(e.target.value)}
              placeholder="Seu nome (para registrar o aceite)"
              className="flex-1 h-11 px-3 border border-slate-300 rounded-md text-sm"
              data-testid="public-accept-name"
            />
            <button
              onClick={handlePrint}
              className="h-11 px-4 rounded-md border border-slate-300 text-slate-700 font-medium flex items-center justify-center gap-2 hover:bg-slate-50"
              data-testid="public-print-btn"
            >
              <Printer className="w-4 h-4" /> Baixar PDF
            </button>
            <button
              onClick={handleAccept}
              disabled={accepting}
              className="h-11 px-6 rounded-md bg-green-600 hover:bg-green-700 text-white font-semibold flex items-center justify-center gap-2 disabled:opacity-60"
              data-testid="public-accept-btn"
            >
              {accepting ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
              Aceitar proposta
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
